import { AiFillEye, AiFillDelete } from "react-icons/ai";
import { FaUserMinus } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { isEmpty, startCase } from "lodash";
import { BsArrowUpShort } from "react-icons/bs";
import TableHeaderCell from "components/reusable/TableHeaderCell";
import ConfirmationModal from '../../components/reusable/ConfirmationModal';
import useToastContext from "hooks/useToastContext";
import { apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useState, useEffect } from "react";

const MailManagerTable = ({ 
  employeeList, 
  page, 
  sort, 
  setSort, 
  handleView, 
  allEmployeeList,
  selectedEmployees,
  setSelectedEmployees,
  onOpenEmailModal 
}) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [loadingStates, setLoadingStates] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  // Handle individual checkbox selection
  const handleCheckboxChange = (employeeId) => {
    if (!employeeId) return; // Skip if no employee ID
    
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedEmployees([]);
      setSelectAll(false);
    } else {
      // Select all current page employees
      const allIds = employeeList
        .map(item => item?.Employee?._id || item?.Employee?.id || item?._id || item?.id)
        .filter(id => id && id !== undefined && id !== null);
      
      if (allIds.length > 0) {
        setSelectedEmployees(allIds);
        setSelectAll(true);
      }
    }
  };

  // Update select all state when selectedEmployees or employeeList changes
  useEffect(() => {
    if (employeeList && employeeList.length > 0) {
      const allIds = employeeList
        .map(item => item?.Employee?._id || item?.Employee?.id || item?._id || item?.id)
        .filter(id => id && id !== undefined && id !== null);
      
      if (allIds.length === 0) {
        setSelectAll(false);
        return;
      }

      // Check if all current page items are selected
      const allSelected = allIds.every(id => selectedEmployees.includes(id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedEmployees, employeeList]);

  const handleSort = (sortByValue, sortTypeValue) => {
    const newSortType = sort.sortType === "asc" ? "desc" : "asc";
    setSort({
      sortBy: sortByValue,
      sortType: sortByValue === sort.sortBy ? newSortType : sortTypeValue,
    });
  };

  return (
    <div>
      <div className="p-3">
        <div className="overflow-x-auto rounded-lg border relative ">
          <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400 ">
            <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <TableHeaderCell text={t("SR_NO")} />
                <TableHeaderCell text={t("EMPLOYEE_ID")} />
                <TableHeaderCell text={t("EMPLOYEE_NAME")} />
                <TableHeaderCell text={t("DEPARTMENT")} />
                <TableHeaderCell text={t("JOB_TITLE")} />
                <TableHeaderCell text={t("YEAR_OF_EXPERIENCE")} />

                <th scope="col" onClick={() => handleSort("Employee.email", "asc")} className="py-3 cursor-pointer px-6 ">
                  <div className="flex justify-start">
                    <span>{t("EMAIL_ID")}</span>
                    <span>
                      {sort.sortType === "asc" && sort.sortBy === "Employee.email" && <BsArrowUpShort className="w-4 h-4" />}
                      {sort.sortType === "desc" && sort.sortBy === "Employee.email" && <BsArrowUpShort className="h-4 rotate-180 w-4 " />}
                    </span>
                  </div>
                </th>

                <th onClick={() => handleSort("acceptedRejectedAt", "asc")} scope="col" className="py-3 px-6 cursor-pointer">
                  <div className="flex justify-start">
                    <span className="w-20">{t("HIRE_DATE")}</span>
                    <span>
                      {sort.sortBy === "acceptedRejectedAt" && sort.sortType === "asc" && <BsArrowUpShort className="w-4 h-4" />}
                      {sort.sortBy === "acceptedRejectedAt" && sort.sortType === "desc" && <BsArrowUpShort className="rotate-180 w-4 h-4 " />}
                    </span>
                  </div>
                </th>
                <th scope="col" className="py-3 px-6 text-center">
                  {t("STATUS")}
                </th>
                <th scope="col" className="py-3 px-6 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <span>{t("O_ACTION")}</span>
                    {/* Select All Checkbox in Action Column Header */}
                    <input
                    title="Select all"
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {employeeList &&
                employeeList.map((item, i) => {
                  const employeeId = item?.Employee?._id || item?.Employee?.id || item?._id || item?.id;
                  const isSelected = employeeId ? selectedEmployees.includes(employeeId) : false;
                  
                  return (
                    <tr key={i} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                      <th scope="row" className="py-4 px-6 border-r font-medium text-gray-900  dark:text-white">
                        {i + 1 + 10 * (page - 1)}
                      </th>
                      <td className="border-r py-4 px-6 text-gray-900">{item?.Employee?.employeeId}</td>
                      <td className="text-gray-900 py-4 px-6 border-r">{item?.Employee?.name}</td>
                      <td className="py-4 border-r px-6  text-gray-900">{item?.Department?.name}</td>
                      <td className="py-4 px-6 border-r text-gray-900">{item?.Job?.name}</td>
                      <td className="border-r py-4 px-6 text-gray-900">{item?.yearOfExperiece}</td>
                      <td className="px-6 border-r py-4 text-gray-900">{item?.Employee?.email}</td>

                      <td className="py-4 text-left text-gray-900 px-6 border-r ">{item?.acceptedRejectedAt ? dayjs(item?.acceptedRejectedAt).format("MM-DD-YYYY h:mm A") : "N/A"} </td>
                      <td className="py-4 text-gray-900 px-6 border-r ">{startCase(item?.formStatus)}</td>

                      <td className="py-4 text-gray-900 px-6 border-l ">
                        <div className="flex justify-center items-center">
                          <ul className="flex items-center gap-3">
                            <li className="hover:bg-white px-2 hover:text-LightBlue py-2">
                              <a title="View employee" onClick={() => handleView(item)} className="cursor-pointer">
                                <AiFillEye className="w-5 h-5 text-slate-600 hover:text-LightBlue" />
                              </a>
                            </li>
                            <li>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCheckboxChange(employeeId)}
                                disabled={!employeeId}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                              />
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {isEmpty(employeeList) ? (
                <tr className="text-center dark:bg-gray-800 bg-white border-b dark:border-gray-700">
                  <td className="py-4 px-6 border-r" colSpan={10}>
                    {t("O_NO_RECORD_FOUND")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MailManagerTable;